/**
 * API Service — HTTP Transport Layer
 *
 * Single responsibility: fire authenticated HTTP requests.
 * Route resolution lives in routerResolver.ts.
 * This file must not contain any routing logic.
 */

import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { SecureStorage } from './SecureStorage';
import { loadRouterManifest as _loadRouterManifest, resolveRouterEndpoint } from './routerResolver';
import { captureRouteContext } from './routeContext';
import { store } from '../app/store';
import { setActiveError } from '../app/redux/diagnostics/diagnosticsSlice';

// Re-export so existing App.tsx callers don't need to change their import path
export { loadRouterManifest } from './routerResolver';

/**
 * Flags a failed request into diagnosticsSlice so PlatformHeader's bug-report
 * icon can go "active" automatically, independent of auth/org state. Separate
 * concern from auth: a failing request here does NOT mean JWT/org context is
 * wrong (see docs/agent-contracts/PATTERN_REDUX_AUTH_DEPENDENCY_STANDARD.md).
 */
const flagApiError = (
    url: string,
    error: any,
    routeSnapshot: ReturnType<typeof captureRouteContext>,
) => {
    const status: number | undefined = error?.response?.status;
    const errorType = !status ? 'Network Error' : status >= 500 ? '500 Error' : '400 Error';
    const detail = error?.response?.data?.detail;
    // FastAPI 422 validation errors return `detail` as an array of objects (Pydantic
    // default shape), not a string. Stringifying it via template-literal interpolation
    // produces the useless literal "[object Object]" -- serialize non-string detail
    // shapes explicitly instead.
    const message =
        typeof detail === 'string'
            ? detail
            : detail
              ? JSON.stringify(detail)
              : error?.message || 'Unknown API error';

    store.dispatch(
        setActiveError({
            errorType,
            statusCode: status ?? null,
            message: `${url} → ${message}`,
            ...routeSnapshot,
            timestamp: new Date().toISOString(),
        }),
    );
};

// API Base URL from environment (Azure Portal environment variables injected at build time)
const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Suppress unused-import warning — _loadRouterManifest re-exported above
void _loadRouterManifest;

// HTTP Methods type guard
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

const getAccessToken = () => SecureStorage.getAccessToken();
const getRefreshToken = () => SecureStorage.getRefreshToken();

const setAccessToken = (token: string) => SecureStorage.setAccessToken(token);

const refreshToken = async (): Promise<string | null> => {
    try {
        // Backend's /api/auth/refresh reads the refresh token from the Authorization
        // Bearer header (Depends(security) -> HTTPAuthorizationCredentials), NOT the
        // request body. Sending it in the body (as before) meant `credentials` was
        // always empty server-side, so every refresh attempt silently failed with 401 -
        // confirmed 2026-10-12 while investigating a stuck-spinner bug.
        const token = getRefreshToken();
        const res = await axios.post(
            `${BASE_URL}/api/auth/refresh`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );

        const newToken = res.data.access_token;
        if (newToken) {
            setAccessToken(newToken);
            return newToken;
        }
        return null;
    } catch (error) {
        console.error('Token refresh failed', error);
        return null;
    }
};

/**
 * Centralized API request helper with automatic JWT authentication and token refresh.
 *
 * @template T - Expected response data type
 * @param url - API endpoint path (relative to BASE_URL, must start with /)
 * @param method - HTTP method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
 * @param data - Request body data (automatically serialized to JSON)
 *
 * @returns Promise<T> - Parsed response data
 *
 * @example
 * // Correct usage:
 * await apiRequest('/api/procedures/discovery', 'POST', { manage: 4 })
 * await apiRequest<ProcedureData[]>('/api/procedures/discovery', 'POST', { Manage: 5 })
 * await apiRequest('/api/procedures/code/123', 'GET')
 *
 * @example
 * // WRONG - DO NOT DO THIS:
 * await apiRequest('/api/endpoint', { method: 'POST', body: {...} })  // ❌ Object as 2nd param
 * await apiRequest('/api/endpoint', 'POST', JSON.stringify({...}))    // ❌ Manual JSON.stringify
 * await fetch('/api/endpoint', { headers: { Authorization: ... }})    // ❌ Direct fetch()
 *
 * @remarks
 * - Automatically includes JWT Bearer token from localStorage
 * - Handles 401 Unauthorized with automatic token refresh
 * - Redirects to /login if refresh fails
 * - Content-Type: application/json set automatically
 * - Base URL from VITE_API_BASE_URL environment variable
 */
export const apiRequest = async <T>(
    url: string,
    method: HttpMethod = 'GET',
    data?: any,
): Promise<T> => {
    const accessToken = getAccessToken();

    // Snapshot route context NOW (request-send time), not at catch-time. If the user
    // navigates to a different tab before this request settles, catch-time ambient
    // route reads would misattribute the error to the NEW tab while the message still
    // shows the OLD request's URL - a stale-attribution bug (see /memories/repo notes
    // on tasks 7999/7996, 2026-09-08).
    const routeSnapshot = captureRouteContext();

    // Check if token exists - RouteSecurityWrapper handles redirect, we just reject
    if (!accessToken) {
        console.warn(
            '[apiService] No access token - API call rejected (RouteSecurityWrapper will handle redirect)',
        );
        // Return rejected promise to prevent further execution
        // RouteSecurityWrapper is responsible for the redirect, not apiService
        return Promise.reject(new Error('No access token'));
    }

    const config: AxiosRequestConfig = {
        method: method,
        url: `${BASE_URL}${url}`,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        data: data,
        // Without a timeout, a request that never gets a response (e.g. a cold
        // backend/DB connection right after a hard refresh) leaves its promise
        // pending forever. Any caller's `loading` state stays stuck true forever
        // too (its `finally` block never runs) - the only "fix" being a full page
        // reload, which abandons the hung connection. Confirmed root cause of a
        // permanently-spinning tab, 2026-10-12. 20s is generous enough for normal
        // slow queries while still failing fast enough to recover automatically.
        timeout: 20000,
    };

    try {
        const response: AxiosResponse<T> = await axios(config);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 401) {
            console.warn('[apiService] 401 Unauthorized - attempting token refresh');
            const newToken = await refreshToken();
            if (newToken) {
                console.log('[apiService] Token refreshed successfully, retrying request');
                config.headers = {
                    ...config.headers,
                    Authorization: `Bearer ${newToken}`,
                };
                const retryResponse: AxiosResponse<T> = await axios(config);
                return retryResponse.data;
            } else {
                console.error('[apiService] Token refresh failed - clearing tokens');
                // Clear expired tokens - RouteSecurityWrapper will detect and redirect on next check
                SecureStorage.clearTokens();
                // Trigger a storage event so RouteSecurityWrapper can react immediately
                window.dispatchEvent(new Event('storage'));
                throw new Error('Token refresh failed');
            }
        }
        flagApiError(url, error, routeSnapshot);
        throw error;
    }
};

/**
 * Sequence-based API request helper (eliminates hardcoded API paths)
 *
 * PATTERN: TSX component references router by sequence + ordinal
 * - Sequence: Component @tg-seq (e.g., 10031)
 * - Ordinal: Router number for that component (1, 2, 3...)
 * - Manifest: Resolves tsx_{seq}_{ordinal}.py → /api/endpoint
 *
 * @template T - Expected response data type
 * @param tsxSeq - Component sequence number (from @tg-seq annotation)
 * @param ordinal - Router ordinal (1, 2, 3... for multiple routers per component)
 * @param data - Request body data (automatically serialized to JSON)
 * @param method - HTTP method (defaults to POST)
 * @param prefix - Router prefix taxonomy (tsx=standard, sec=secure VPN, etc.)
 *
 * @returns Promise<T> - Parsed response data
 *
 * @example
 * // Component: scr_tgc_10833 (@tg-seq: 10833, @tg-api-binding: 10833-1)
 * await apiRequestBySeq(10833, 1, { manage: 5 })  // Calls tsx_10833_1.py → /api/tsx/10833/1
 * await apiRequestBySeq(10833, 2, { manage: 8 })  // Calls tsx_10833_2.py → /api/tsx/10833/2
 *
 * @example
 * // Future: Secure endpoint with VPN prefix
 * await apiRequestBySeq(10833, 1, { manage: 5 }, 'POST', 'sec')  // → /api/sec/10833/1
 *
 * @remarks
 * - Pure sequence-based routing (parallel to componentResolver pattern)
 * - Endpoint calculated, not looked up: /api/{prefix}/{seq}/{ordinal}
 * - Python filename: tsx_{TSX_Seq_Num}_{Router_Ordinal}.py (stored in tsxbound/)
 * - Router_Registry metadata enables authorization checks (360° Authentication)
 * - Prefix taxonomy enables security boundaries (standard, secure VPN, etc.)
 */
export const apiRequestBySeq = async <T>(
    tsxSeq: number,
    ordinal: number,
    data?: any,
    method: HttpMethod = 'POST',
    prefix: string = 'tsx',
): Promise<T> => {
    // Calculate endpoint (pure sequence pattern)
    const endpoint = resolveRouterEndpoint(tsxSeq, ordinal, prefix);

    // Delegate to standard apiRequest
    return apiRequest<T>(endpoint, method, data);
};
